import { useEffect, useState } from "react";
import { Phone, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";
import { openCheckout } from "@/lib/razorpay";

export default function MobileSettings() {
  const [balance, setBalance] = useState({ purchased: 0, used: 0, balance: 0 });
  const [packs, setPacks] = useState([]);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);   // pack_id currently being purchased

  const loadAll = async () => {
    try {
      const [b, p, u] = await Promise.all([
        http.get("/mobile-credits/balance"),
        http.get("/mobile-credits/packs"),
        http.get("/mobile-credits/usage").catch(() => ({ data: { lookups: [] } })),
      ]);
      setBalance(b.data);
      setPacks(p.data.packs || []);
      setUsage(u.data.lookups || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const buyPack = async (pack) => {
    setBuying(pack.id);
    try {
      const { data } = await http.post("/mobile-credits/checkout", { pack_id: pack.id });
      if (data.demo) {
        toast.success(`+${pack.credits.toLocaleString()} mobile credits added (demo)`);
        await loadAll();
        setBuying(null);
        return;
      }
      if (data.affiliate && data.affiliate.applied) {
        toast.success(
          `${data.affiliate.partner_name} discount applied · ${data.affiliate.discount_pct}% off ($${data.affiliate.discount_amount_usd.toFixed(2)} saved)`,
          { duration: 6000 }
        );
      }
      // Surface GST breakdown before Razorpay opens so the customer isn't surprised.
      if (data.tax && (data.tax.tax_amount_usd || 0) > 0) {
        toast.info(
          `Charging $${data.tax.total_usd.toFixed(2)} · $${data.tax.subtotal_usd.toFixed(2)} + ${data.tax.tax_label} $${data.tax.tax_amount_usd.toFixed(2)}`,
          { duration: 5000 }
        );
      }
      const rzp = data.razorpay;
      await openCheckout({
        key: rzp.key_id,
        amount: rzp.amount,
        currency: rzp.currency,
        order_id: rzp.order_id,
        name: rzp.name,
        description: rzp.description,
        prefill: rzp.prefill,
        theme: { color: "#002FA7" },
        modal: { ondismiss: () => setBuying(null) },
        handler: async (resp) => {
          try {
            await http.post("/mobile-credits/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`+${pack.credits.toLocaleString()} mobile credits added`);
            await loadAll();
          } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Verification failed");
          } finally {
            setBuying(null);
          }
        },
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start checkout");
      setBuying(null);
    }
  };

  return (
    <main className="max-w-4xl mx-auto px-6 py-10">
      <div className="mb-8">
        <div className="text-[10px] uppercase font-mono tracking-widestx text-emerald-700 mb-2">
          Find Mobile · Add-on
        </div>
        <h1 className="font-display text-3xl font-extrabold text-ink-900 tracking-tight">
          Reveal verified mobile numbers
        </h1>
        <p className="text-sm text-ink-600 mt-2 max-w-2xl leading-relaxed">
          Get direct mobile numbers for the buying committee. Charged only when we find a valid
          mobile — refunded on miss. Credits never expire.
        </p>
      </div>

      {/* Balance card */}
      <div
        data-testid="mobile-balance-card"
        className="bg-white border-2 border-emerald-200 rounded-sm p-6 mb-8 flex items-center justify-between gap-6 flex-wrap"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-sm bg-emerald-50 flex items-center justify-center">
            <Phone className="w-7 h-7 text-emerald-700" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono tracking-widestx text-ink-400 mb-1">
              Current balance
            </div>
            <div className="font-mono text-4xl font-extrabold text-ink-900 leading-none">
              {loading ? "—" : balance.balance}
              <span className="text-lg text-ink-400 font-normal ml-2">credits</span>
            </div>
            <div className="text-[11px] text-ink-500 font-mono mt-1">
              {balance.used} used · {balance.purchased} lifetime purchased
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase font-mono tracking-widestx text-ink-400">
            1 credit = 1 mobile reveal
          </div>
          <div className="text-xs text-ink-600 mt-1">Charged only on successful reveal</div>
        </div>
      </div>

      {/* Pack shelf */}
      <div className="mb-8">
        <h2 className="font-display font-extrabold text-lg text-ink-900 mb-4">Buy credits</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {packs.map((p) => {
            const perUnit = (p.price_usd / p.credits).toFixed(2);
            const recommended = p.id === "mobile_50";
            const isBuying = buying === p.id;
            return (
              <button
                key={p.id}
                data-testid={`mobile-pack-${p.id}`}
                onClick={() => buyPack(p)}
                disabled={!!buying}
                className={`text-left border rounded-sm p-4 transition-all hover:shadow-sm disabled:opacity-60 disabled:cursor-not-allowed ${
                  recommended
                    ? "border-emerald-500 bg-emerald-50/40"
                    : "border-ink-200 bg-white hover:border-emerald-300"
                }`}
              >
                {recommended && (
                  <div className="text-[9px] uppercase font-mono tracking-widestx text-emerald-700 mb-2">
                    ★ Most popular
                  </div>
                )}
                <div className="font-display font-extrabold text-xl text-ink-900">
                  {p.credits.toLocaleString()}
                </div>
                <div className="text-[11px] font-mono text-ink-500 mb-3">mobiles</div>
                <div className="text-2xl font-mono font-bold text-ink-900">
                  ${p.price_usd}
                </div>
                <div className="text-[10px] font-mono text-ink-400 mt-1">
                  ${perUnit} per mobile
                </div>
                {isBuying && (
                  <div className="mt-3 flex items-center gap-1.5 text-[10px] uppercase font-mono tracking-widestx text-emerald-700">
                    <Loader2 className="w-3 h-3 animate-spin" /> Opening checkout…
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-ink-500 mt-3">
          Secure checkout via Razorpay · Credits never expire · Charged only on successful reveals
        </p>
      </div>

      {/* Recent usage */}
      <div>
        <h2 className="font-display font-extrabold text-lg text-ink-900 mb-4">Recent activity</h2>
        {usage.length === 0 ? (
          <div className="text-sm text-ink-400 italic border border-dashed border-ink-200 rounded-sm p-8 text-center">
            No mobile reveals yet. Click "Find mobile" on any enriched lead to try it.
          </div>
        ) : (
          <div className="bg-white border border-ink-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-[10px] uppercase font-mono tracking-widestx text-ink-500">
                <tr>
                  <th className="text-left px-4 py-2">Contact</th>
                  <th className="text-left px-4 py-2">Company</th>
                  <th className="text-left px-4 py-2">Result</th>
                  <th className="text-left px-4 py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {usage.map((u) => (
                  <tr key={u.id} className="border-t border-ink-100">
                    <td className="px-4 py-3">{u.contact_name || "—"}</td>
                    <td className="px-4 py-3 text-ink-600">{u.company || "—"}</td>
                    <td className="px-4 py-3">
                      {u.state === "settled" ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono text-emerald-700">
                          <CheckCircle2 className="w-3 h-3" /> revealed · 1 cr
                        </span>
                      ) : (
                        <span className="text-[11px] font-mono text-ink-400">
                          no mobile · refunded
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[11px] font-mono text-ink-400">
                      {(u.settled_at || u.created_at || "").slice(0, 10)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {loading && (
        <div className="fixed bottom-6 right-6 bg-white border border-ink-200 rounded-sm px-3 py-2 flex items-center gap-2 text-xs text-ink-500 shadow-lg">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      )}
    </main>
  );
}
